// src/Pages/ValmikiNavbar.jsx
// Shared VALMIKI AI LAB header / navigation.
// Self-contained: this file carries its own CSS in the `css` string below.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import valmiki_logo from "../images/valmiki_logo.JPG";
import Vamili_word from "../images/Vamili_word.JPG";

/* ───────────────────────── STYLES (in-file) ───────────────────────── */

const css = `@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=IBM+Plex+Sans:wght@400;500;600&family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,500;0,6..72,600;1,6..72,400&display=swap');
.vl-header{
  --ink:#0A1729; --ink-2:#132238; --ink-3:#071426;
  --paper:#F3F5F8; --white:#FFFFFF;
  --gold:#C8A24A; --gold-hi:#E0BC62; --gold-deep:#7D5F12;
  --slate:#4F5C73; --on-ink:#F3F5F8; --on-ink-mute:rgba(243,245,248,.72);
  --line:rgba(11,22,38,.14); --line-ink:rgba(243,245,248,.16);
  --danger:#A32626;
  --vl-gap-logo:14px;  /* visible space between the icon artwork and the wordmark artwork */
  --vl-gap-nav:36px;   /* visible space between the wordmark artwork and the HOME link */
  --serif:'Newsreader',Georgia,'Times New Roman',serif;
  --sans:'IBM Plex Sans','Segoe UI',Arial,sans-serif;
  --mono:'IBM Plex Mono',ui-monospace,Menlo,Consolas,monospace;
  font-family:var(--sans); line-height:1.6; -webkit-font-smoothing:antialiased;
}
.vl-header, .vl-header *{box-sizing:border-box;}
:where(.vl-header) :where(p,ul,ol){margin:0;padding:0;}
:where(.vl-header) :where(ul,ol){list-style:none;}
.vl-header :focus-visible{outline:2px solid var(--gold-hi);outline-offset:3px;}
.vl-wrap{max-width:1280px;margin:0 auto;padding:0 32px;}
@media (max-width:560px){.vl-wrap{padding:0 20px;}}
.vl-btn{
  --fill:rgba(200,162,74,.18); --edge:rgba(200,162,74,.45); --line:var(--gold-hi);
  --wipe:var(--gold); --hover-text:var(--ink); --text:var(--on-ink);
  position:relative;overflow:hidden;isolation:isolate;
  display:inline-flex;align-items:center;justify-content:center;padding:14px 28px;border-radius:2px;
  font:600 15px/1 var(--sans);text-decoration:none;cursor:pointer;color:var(--text);
  border:1px solid var(--edge);
  background-color:var(--fill);
  background-image:linear-gradient(var(--wipe),var(--wipe));
  background-repeat:no-repeat;background-origin:border-box;background-position:left center;background-size:0% 100%;
  transition:background-size .45s cubic-bezier(.65,0,.35,1),color .18s ease .13s,border-color .3s ease;
}
.vl-btn::before,.vl-btn::after{content:"";position:absolute;left:0;width:100%;height:3px;pointer-events:none;}
.vl-btn::before{top:0;background:linear-gradient(90deg,transparent 70%,var(--line) 70%);
  animation:vl-run-top 2.4s cubic-bezier(.65,0,.35,1) var(--vl-runs,infinite);}
.vl-btn::after{bottom:0;background:linear-gradient(90deg,var(--line) 30%,transparent 30%);
  animation:vl-run-bottom 2.4s cubic-bezier(.65,0,.35,1) var(--vl-runs,infinite);}
@keyframes vl-run-top{0%{transform:translateX(-100%);}60%,100%{transform:translateX(100%);}}
@keyframes vl-run-bottom{0%{transform:translateX(100%);}60%,100%{transform:translateX(-100%);}}
.vl-btn:hover,.vl-btn:focus-visible{background-size:100% 100%;color:var(--hover-text);border-color:var(--wipe);--line:var(--hover-text);}
@media (prefers-reduced-motion:reduce){
  .vl-btn,.vl-toggle{transition:none;}
  .vl-btn::before,.vl-btn::after{animation:none;}
}

/* header */
.vl-header{position:sticky;top:0;z-index:50;background:#0A1729;border-bottom:1px solid var(--line-ink);}
.vl-header-row{display:flex;align-items:center;justify-content:space-between;min-height:88px;gap:24px;}

/* brand: icon on the left, wordmark on the right (side by side).
   The logo files are JPGs with their own dark navy backgrounds. mix-blend-mode:lighten
   makes every pixel darker than the header take the header colour, so the image
   backgrounds disappear and only the gold / white / blue artwork remains. */
.vl-brand{display:inline-flex;align-items:center;text-decoration:none;color:var(--on-ink);
  flex-shrink:0;white-space:nowrap;margin-right:var(--vl-gap-nav);}
.vl-brand-mark{display:inline-flex;flex-direction:row;align-items:center;gap:var(--vl-gap-logo);}
/* Each JPG carries empty navy padding around the artwork (measured as a fraction of image height).
   The negative margins below cancel that padding, so --vl-gap-logo and --vl-gap-nav are the
   real visible distances between the artwork itself. */
.vl-brand-logo{--h:60px;height:var(--h);width:auto;object-fit:contain;display:block;mix-blend-mode:lighten;
  margin-left:calc(var(--h) * -.189);margin-right:calc(var(--h) * -.245);}
.vl-brand-word{--h:40px;height:var(--h);width:auto;object-fit:contain;display:block;mix-blend-mode:lighten;
  margin-left:calc(var(--h) * -.29);margin-right:calc(var(--h) * -.284);}

.vl-nav{display:flex;align-items:center;gap:18px;margin-left:auto;flex-wrap:nowrap;}
.vl-nav a:not(.vl-btn){font-size:12.5px;font-weight:500;letter-spacing:.09em;text-decoration:none;color:var(--on-ink-mute);
  padding:6px 0;border-bottom:1.5px solid transparent;white-space:nowrap;transition:color .15s,border-color .15s;}
.vl-nav a:not(.vl-btn):hover{color:var(--on-ink);}
.vl-nav a[aria-current="page"]{color:var(--on-ink);border-bottom-color:var(--gold);}
.vl-nav .vl-btn{padding:11px 18px;font-size:12px;letter-spacing:.08em;white-space:nowrap;flex-shrink:0;}
.vl-nav .vl-btn,.vl-mobile .vl-btn,.vl-quick .vl-btn{--fill:#162B47;--edge:var(--gold);--line:var(--gold);--text:var(--on-ink);--wipe:var(--gold);--hover-text:var(--ink);
  box-shadow:0 6px 14px rgba(0,0,0,.35);
  animation:vl-bg-breathe 4s ease-in-out infinite;}
@keyframes vl-bg-breathe{
  0%,100%{background-color:#162B47;box-shadow:0 6px 14px rgba(0,0,0,.35);}
  50%{background-color:#24426B;box-shadow:0 6px 18px rgba(200,162,74,.30);}
}
@media (prefers-reduced-motion:reduce){
  .vl-nav .vl-btn,.vl-mobile .vl-btn,.vl-quick .vl-btn{animation:none;}
}

.vl-quick{display:none;align-items:center;gap:10px;flex-shrink:0;}
.vl-quick a{font-size:11.5px;font-weight:500;letter-spacing:.07em;text-decoration:none;color:var(--on-ink-mute);
  white-space:nowrap;transition:color .15s;}
.vl-quick a:hover{color:var(--on-ink);}
.vl-quick .vl-btn{padding:9px 14px;font-size:11px;letter-spacing:.07em;white-space:nowrap;}

.vl-toggle{display:none;background-color:transparent;
  background-image:linear-gradient(var(--paper),var(--paper));background-repeat:no-repeat;
  background-origin:border-box;background-position:left center;background-size:0% 100%;
  border:1px solid var(--line-ink);color:var(--on-ink);
  width:42px;height:42px;border-radius:3px;font-size:20px;cursor:pointer;flex-shrink:0;
  transition:background-size .45s cubic-bezier(.65,0,.35,1),color .18s ease .13s;}
.vl-toggle:hover,.vl-toggle:focus-visible{background-size:100% 100%;color:var(--ink);}
.vl-mobile{display:none;background:#0A1729;border-top:1px solid var(--line-ink);padding:8px 24px 22px;}
.vl-mobile a:not(.vl-btn){display:block;padding:14px 0;border-bottom:1px solid var(--line-ink);text-decoration:none;
  font-size:14px;letter-spacing:.08em;color:var(--on-ink);}

/* switch to the compact header before the desktop nav can run out of room.
   Raised to 1400px so the extra FOUNDER 360 link never overflows. */
@media (max-width:1400px){
  .vl-nav{display:none;}
  .vl-quick{display:flex;}
  .vl-toggle{display:block;}
  .vl-mobile.open{display:block;}
  .vl-brand{margin-right:0;}
}
/* mobile: logo icon + wordmark stay visible, close together.
   The top-bar CONTACT button is removed; "CONTACT WITH US" (form) is a button inside the ☰ menu,
   and the CONTACT page link is in the menu list. */
.vl-mobile-partner,.vl-mobile-contact{display:none;}
.vl-mobile-contact .vl-btn{width:100%;margin-top:18px;}
@media (max-width:720px){
  .vl-header{--vl-gap-logo:6px;}
  .vl-header-row{gap:10px;}
  .vl-brand-logo{--h:min(44px,10.5vw);}
  .vl-brand-word{--h:min(30px,7.2vw);}
  .vl-quick .vl-btn{display:none;}
  .vl-mobile-contact{display:block;}
}
@media (max-width:560px){
  .vl-quick{display:none;}                 /* PARTNER WITH US moves into the ☰ menu */
  .vl-mobile-partner{display:block;}
}

/* ── Contact popup / modal ──
   The popup carries its own tokens, reset and box-sizing, so it looks identical
   wherever it is mounted (inside the header here, or inside a page such as Founder 360). */
@keyframes vl-modal-in{from{opacity:0;}to{opacity:1;}}
@keyframes vl-modal-pop{from{opacity:0;transform:translateY(12px) scale(.97);}to{opacity:1;transform:none;}}
.vl-modal-backdrop{
  --ink:#0A1729; --paper:#F3F5F8; --white:#FFFFFF;
  --gold:#C8A24A; --gold-hi:#E0BC62; --gold-deep:#7D5F12;
  --slate:#4F5C73; --on-ink:#F3F5F8; --danger:#A32626;
  --serif:'Newsreader',Georgia,'Times New Roman',serif;
  --sans:'IBM Plex Sans','Segoe UI',Arial,sans-serif;
  --mono:'IBM Plex Mono',ui-monospace,Menlo,Consolas,monospace;
  font-family:var(--sans);line-height:1.6;-webkit-font-smoothing:antialiased;
  position:fixed;inset:0;z-index:200;background:rgba(8,17,30,.72);
  backdrop-filter:blur(3px);display:flex;align-items:center;justify-content:center;padding:24px;
  animation:vl-modal-in .2s ease;}
.vl-modal-backdrop, .vl-modal-backdrop *{box-sizing:border-box;}
.vl-modal-backdrop :where(h2,p){margin:0;}
.vl-modal-backdrop :focus-visible{outline:2px solid var(--gold-deep);outline-offset:3px;}
.vl-modal{position:relative;width:100%;max-width:520px;max-height:92vh;overflow-y:auto;
  background:var(--white);color:var(--ink);
  border-radius:10px;padding:36px 36px 32px;box-shadow:0 30px 80px rgba(0,0,0,.45);
  animation:vl-modal-pop .25s cubic-bezier(.2,.7,.2,1);}
.vl-modal h2{font-family:var(--serif);font-weight:500;font-size:1.75rem;line-height:1.2;
  margin:0 0 8px;letter-spacing:-.01em;color:var(--ink);}
.vl-modal p.vl-modal-sub{font-size:15px;line-height:1.6;color:var(--slate);margin:0 0 24px;}
.vl-modal-close{position:absolute;top:14px;right:14px;width:34px;height:34px;border-radius:50%;
  border:1px solid rgba(11,22,38,.15);background:transparent;color:var(--ink);font-size:18px;
  cursor:pointer;display:inline-flex;align-items:center;justify-content:center;
  transition:background .18s,color .18s;}
.vl-modal-close:hover{background:var(--ink);color:var(--white);}
.vl-modal form{display:grid;gap:14px;}
.vl-modal label{display:block;font-size:13px;font-weight:600;color:var(--ink);margin-bottom:6px;
  letter-spacing:.02em;}
.vl-modal input,.vl-modal textarea{
  width:100%;padding:12px 14px;font:400 14px/1.4 var(--sans);
  border:1px solid rgba(11,22,38,.18);border-radius:3px;background:var(--white);color:var(--ink);
  transition:border-color .18s,box-shadow .18s;}
.vl-modal input:focus,.vl-modal textarea:focus{
  outline:none;border-color:var(--gold);box-shadow:0 0 0 3px rgba(200,162,74,.18);}
.vl-modal textarea{resize:vertical;min-height:96px;}
/* the submit button sits on a white card, so it uses the navy + gold look
   (the default .vl-btn has light text, which is unreadable on white) */
.vl-modal .vl-btn{--fill:#162B47;--edge:var(--gold);--text:var(--on-ink);--wipe:var(--gold);--hover-text:var(--ink);
  width:100%;justify-content:center;margin-top:6px;}
.vl-modal .vl-err{font-size:13px;color:var(--danger);margin-top:4px;font-weight:500;}
.vl-modal .vl-ok{font-size:14px;color:#1B6A2E;font-weight:500;padding:10px 12px;
  border-left:3px solid #1B6A2E;background:#F2FBF4;border-radius:3px;}
@media (max-width:560px){
  .vl-modal{padding:28px 22px 24px;}
  .vl-modal h2{font-size:1.5rem;}
}
@media (prefers-reduced-motion:reduce){
  .vl-modal-backdrop,.vl-modal{animation:none;}
}

/* ── Math CAPTCHA ── */
.vl-captcha{display:flex;align-items:center;gap:12px;flex-wrap:wrap;
  padding:14px;border:1px solid rgba(11,22,38,.14);border-radius:6px;background:var(--paper);}
.vl-captcha-q{font:600 15px/1.2 var(--mono);color:var(--ink);letter-spacing:.03em;
  display:inline-flex;align-items:center;gap:8px;}
.vl-captcha-q b{color:var(--gold-deep);font-size:1.15rem;}
.vl-captcha input{width:90px;padding:10px 12px;font:600 15px/1.2 var(--mono);
  border:1px solid rgba(11,22,38,.22);border-radius:3px;background:var(--white);}
.vl-captcha input:focus{outline:none;border-color:var(--gold);box-shadow:0 0 0 3px rgba(200,162,74,.18);}
.vl-captcha-refresh{margin-left:auto;background:transparent;border:1px solid rgba(11,22,38,.18);
  border-radius:3px;padding:8px 12px;font:500 12px/1 var(--sans);color:var(--ink);cursor:pointer;
  transition:background .15s,color .15s;}
.vl-captcha-refresh:hover{background:var(--ink);color:var(--white);border-color:var(--ink);}
.vl-captcha-err{font-size:13px;color:var(--danger);font-weight:500;width:100%;}
`;

/* ───────────────────────── ROUTES & LINKS ───────────────────────── */

export const VALMIKI_ROUTES = {
  home: "/",
  about: "/about",
  program: "/program",
  lab: "/lab",
  founder360: "/founder-360",
  partner: "/partner",
  contact: "/contact",
};

export const VALMIKI_NAV_LINKS = [
  { label: "HOME", href: VALMIKI_ROUTES.home },
  { label: "ABOUT VALMIKI", href: VALMIKI_ROUTES.about },
  { label: "PROGRAM", href: VALMIKI_ROUTES.program },
  { label: "VALMIKI AI LAB", href: VALMIKI_ROUTES.lab },
  { label: "FOUNDER 360", href: VALMIKI_ROUTES.founder360 },
  { label: "PARTNER WITH US", href: VALMIKI_ROUTES.partner },
  { label: "CONTACT", href: VALMIKI_ROUTES.contact },
];

const VALMIKI_ALWAYS_VISIBLE_LABELS = ["PARTNER WITH US"];

export const ValmikiBrandMark = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" aria-hidden="true">
    <circle cx="15" cy="15" r="14" stroke="#C8A24A" strokeWidth="1.2" />
    <path d="M9 19L15 8L21 19" stroke="#F3F5F8" strokeWidth="1.4" strokeLinejoin="round" />
    <circle cx="15" cy="15.5" r="1.6" fill="#C8A24A" />
  </svg>
);

/* ───────────────────────── LOGO + WORDMARK ───────────────────────── */
/* Order: icon first (left) → wordmark second (right) */

const ValmikiLogo = () => (
  <span className="vl-brand-mark">
    <img
      src={valmiki_logo}
      alt=""
      aria-hidden="true"
      className="vl-brand-logo"
    />
    <img
      src={Vamili_word}
      alt="VALMIKI AI LAB"
      className="vl-brand-word"
    />
  </span>
);

/* ───────────────────────── Math CAPTCHA ───────────────────────── */

function makeCaptcha() {
  const a = 2 + Math.floor(Math.random() * 8);
  const b = 2 + Math.floor(Math.random() * 8);
  return { a, b, answer: a + b };
}

/* ───────────── Contact popup with CAPTCHA ───────────── */

export function ValmikiContactModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [captcha, setCaptcha] = useState(() => makeCaptcha());
  const [captchaInput, setCaptchaInput] = useState("");
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ name: "", email: "", message: "" });
    setCaptcha(makeCaptcha());
    setCaptchaInput("");
    setErrors({});
    setSent(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const refreshCaptcha = () => {
    setCaptcha(makeCaptcha());
    setCaptchaInput("");
  };

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim()))
      next.email = "Enter a valid email address.";
    if (!form.message.trim()) next.message = "Please enter a message.";
    if (Number(captchaInput) !== captcha.answer)
      next.captcha = "Incorrect answer. Please try again.";

    setErrors(next);
    if (Object.keys(next).length) {
      if (next.captcha) refreshCaptcha();
      return;
    }

    // TODO: send `form` to your backend / email service here.
    setSent(true);
    setTimeout(() => onClose(), 1400);
  };

  if (!open) return null;

  return (
    <div
      className="vl-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="vl-modal-title"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="vl-modal">
        <button
          type="button"
          className="vl-modal-close"
          aria-label="Close contact form"
          onClick={onClose}
        >
          ✕
        </button>

        <h2 id="vl-modal-title">Contact with us</h2>
        <p className="vl-modal-sub">
          Tell us a little about you and we&apos;ll get back within 1–2 working days.
        </p>

        {sent ? (
          <p className="vl-ok" role="status">
            Thank you! Your message has been sent.
          </p>
        ) : (
          <form onSubmit={submit} noValidate>
            <div>
              <label htmlFor="vl-m-name">Full name</label>
              <input
                id="vl-m-name"
                type="text"
                autoComplete="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                aria-invalid={errors.name ? "true" : undefined}
              />
              {errors.name && <p className="vl-err">{errors.name}</p>}
            </div>

            <div>
              <label htmlFor="vl-m-email">Email</label>
              <input
                id="vl-m-email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                aria-invalid={errors.email ? "true" : undefined}
              />
              {errors.email && <p className="vl-err">{errors.email}</p>}
            </div>

            <div>
              <label htmlFor="vl-m-msg">Message</label>
              <textarea
                id="vl-m-msg"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                aria-invalid={errors.message ? "true" : undefined}
              />
              {errors.message && <p className="vl-err">{errors.message}</p>}
            </div>

            <div className="vl-captcha">
              <span className="vl-captcha-q" aria-label={`What is ${captcha.a} plus ${captcha.b}?`}>
                What is <b>{captcha.a}</b> + <b>{captcha.b}</b> ?
              </span>
              <input
                type="text"
                inputMode="numeric"
                aria-label="Answer to the captcha"
                value={captchaInput}
                onChange={(e) => setCaptchaInput(e.target.value)}
                aria-invalid={errors.captcha ? "true" : undefined}
              />
              <button
                type="button"
                className="vl-captcha-refresh"
                onClick={refreshCaptcha}
                aria-label="Get a new question"
              >
                ↻ New question
              </button>
              {errors.captcha && <p className="vl-captcha-err">{errors.captcha}</p>}
            </div>

            <button type="submit" className="vl-btn vl-btn-gold">Send message</button>
          </form>
        )}
      </div>
    </div>
  );
}

/* ───────────────────────── NAVBAR ───────────────────────── */

export default function ValmikiNavbar() {
  const [open, setOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const { pathname } = useLocation();

  // stable reference so the modal's Escape-key listener is not re-attached on every render
  const closeContact = useCallback(() => setContactOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const mobileOnlyLinks = useMemo(
    () =>
      VALMIKI_NAV_LINKS.filter(
        (l) => !VALMIKI_ALWAYS_VISIBLE_LABELS.includes(l.label)
      ),
    []
  );

  const isActive = (href) => href === pathname;

  return (
    <header className="vl-header">
      <style>{css}</style>
      <div className="vl-wrap vl-header-row">
        <Link className="vl-brand" to={VALMIKI_ROUTES.home} aria-label="VALMIKI AI LAB home">
          <ValmikiLogo />
        </Link>

        <nav className="vl-nav" aria-label="Main">
          {VALMIKI_NAV_LINKS.map((l) => (
            <Link
              key={l.label}
              to={l.href}
              aria-current={isActive(l.href) ? "page" : undefined}
            >
              {l.label}
            </Link>
          ))}
          <button
            type="button"
            className="vl-btn vl-btn-gold"
            onClick={() => setContactOpen(true)}
          >
            CONTACT WITH US
          </button>
        </nav>

        <div className="vl-quick">
          <Link to={VALMIKI_ROUTES.partner}>PARTNER WITH US</Link>
          <button
            type="button"
            className="vl-btn vl-btn-gold"
            onClick={() => setContactOpen(true)}
          >
            CONTACT
          </button>
        </div>

        <button
          type="button"
          className="vl-toggle"
          aria-expanded={open}
          aria-controls="vl-mobile-menu"
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen(!open)}
        >
          {open ? "✕" : "☰"}
        </button>
      </div>

      <nav id="vl-mobile-menu" className={`vl-mobile${open ? " open" : ""}`} aria-label="Mobile">
        {mobileOnlyLinks.map((l) => (
          <Link key={l.label} to={l.href} onClick={() => setOpen(false)}>
            {l.label}
          </Link>
        ))}

        {/* phones: the top bar has no room for the Partner link, so it lives here */}
        <div className="vl-mobile-partner">
          <Link to={VALMIKI_ROUTES.partner} onClick={() => setOpen(false)}>
            PARTNER WITH US
          </Link>
        </div>

        {/* mobile: the form button lives inside the menu */}
        <div className="vl-mobile-contact">
          <button
            type="button"
            className="vl-btn vl-btn-gold"
            onClick={() => {
              setOpen(false);
              setContactOpen(true);
            }}
          >
            CONTACT WITH US
          </button>
        </div>
      </nav>

      <ValmikiContactModal open={contactOpen} onClose={closeContact} />
    </header>
  );
}